import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { apiHeaders } from "./apiHeaders";
import {
  sessionStorageService,
  localStorageService,
} from "@utils/storageService/StorageService";
import { IContentType } from "../../types/settings/formBuilder/IContentType.interface";

const getBaseQuery = (baseUrl: string, contentType?: IContentType) =>
  fetchBaseQuery({
    baseUrl: baseUrl,
    credentials: "include", //cookies included yop.
    prepareHeaders: (headers) => {
      const practiceGuid = localStorageService.get("fastr_practiceId") || "";
      const sessionId = sessionStorageService.get("fastr_token") || "";

      const headerInfo = {
        practiceGuid,
        userSession: sessionId,
      };

      apiHeaders(headers, headerInfo, contentType, sessionId);
      return headers;
    },
  });

const ErrorList = [401, 403] as FetchBaseQueryError["status"][];

export const baseQueryWithReauth = (
  baseURL: string,
  contentType?: IContentType
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    const baseQuery = getBaseQuery(baseURL, contentType);
    let result = await baseQuery(args, api, extraOptions);

    if (!result.error) return result;

    const isAuthError = (error: FetchBaseQueryError) =>
      ErrorList.includes(error?.status);

    const retryWithRefresh = async () => {
      const maxRetries = 3;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (result.error && !isAuthError(result.error)) break;
        try {
          result = await baseQuery(args, api, extraOptions);
          if (!result.error) return result;
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          return {
            error: {
              status: 401,
              data: "Failed to refresh token",
              refreshError:
                refreshError instanceof Error
                  ? refreshError.message
                  : String(refreshError),
            },
          };
        }
      }
      return result;
    };

    return await retryWithRefresh();
  };
};
